<div class="utilities-container">
  <div class="page-title">Admin Utilities</div>

  <!-- Overlay for processing state -->
  <div *ngIf="isProcessing" class="processing-overlay">
    <div class="processing-spinner">
      <mat-spinner diameter="60"></mat-spinner>
      <p class="processing-message">{{ processingTask }}...</p>

      <ng-container *ngIf="invoiceSessionId && invoiceTotal > 0; else checkEmailProgress">
        <p class="processing-progress">{{ invoiceSent + invoiceSkipped }} of {{ invoiceTotal }} processed</p>
        <p class="processing-detail" *ngIf="invoiceSkipped > 0">{{ invoiceSent }} sent &middot; {{ invoiceSkipped }} skipped</p>
        <p class="processing-current" *ngIf="invoiceCurrentItem">Processing: {{ invoiceCurrentItem }}</p>
        <p class="processing-remaining">{{ invoiceRemaining }} remaining</p>
        <p class="processing-note">You can safely leave this page &mdash; the batch will continue running.</p>
      </ng-container>

      <ng-template #checkEmailProgress>
        <ng-container *ngIf="emailSessionId && emailTotal > 0; else genericNote">
          <p class="processing-progress">{{ emailSent + emailFailed }} of {{ emailTotal }} processed</p>
          <p class="processing-detail" *ngIf="emailFailed > 0">{{ emailSent }} sent &middot; {{ emailFailed }} skipped</p>
          <p class="processing-remaining">{{ emailRemaining }} remaining</p>
          <p class="processing-note">Please leave this page open to monitor progress.</p>
        </ng-container>
      </ng-template>

      <ng-template #genericNote>
        <p class="processing-note">This may take several minutes. Please wait.</p>
      </ng-template>
    </div>
  </div>

  <!-- Alerts Section -->
  <div class="utility-section">
    <div class="section-header">
      <div class="section-title">
        <h2>Alerts</h2>
        <div class="test-mode-container" *ngIf="alertsTestMode">
          <div class="test-mode-badge test-mode-active">
            <mat-icon class="test-mode-icon">warning</mat-icon>
            <span class="test-mode-text">TEST MODE</span>
          </div>
          <div class="test-mode-detail" *ngIf="testEmailSendToAddress">
            <mat-icon class="test-mode-detail-icon">email</mat-icon>
            <span>Emails to: <strong>{{ testEmailSendToAddress }}</strong></span>
          </div>
        </div>
      </div>
    </div>
    <div class="utility-description">
      <p>Run the scheduled alert batch process to send alerts to clients with details of their generated notifications</p>
    </div>
    <div class="button-container">
      <button 
        mat-raised-button 
        color="primary" 
        (click)="previewAlerts()"
        [disabled]="isProcessing || loadingAlertsPreview"
        class="utility-button preview-button">
        <mat-icon>preview</mat-icon>
        Preview Alerts
      </button>
      <button 
        mat-raised-button 
        color="primary" 
        (click)="runNotifications()"
        [disabled]="isProcessing"
        class="utility-button">
        <mat-icon>notifications</mat-icon>
        Run Alerts
      </button>
    </div>

    <!-- Alerts Preview Accordion -->
    <mat-accordion *ngIf="showAlertsPreview" class="preview-accordion">
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>info</mat-icon>
            Alerts Preview ({{ alertsDataSource.data.length }} records)
          </mat-panel-title>
        </mat-expansion-panel-header>
        
        <div *ngIf="loadingAlertsPreview" class="loading-message">
          <mat-spinner diameter="30"></mat-spinner>
          Loading preview...
        </div>

        <div *ngIf="!loadingAlertsPreview && alertsDataSource.data.length > 0">
          <table mat-table [dataSource]="alertsDataSource" matSort #alertsSort class="preview-table">
            <!-- Client Name Column -->
            <ng-container matColumnDef="clientName">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Client</th>
              <td mat-cell *matCellDef="let element">{{ element.clientName }}</td>
            </ng-container>

            <!-- Subscription Name Column -->
            <ng-container matColumnDef="subscriptionName">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Subscription</th>
              <td mat-cell *matCellDef="let element">{{ element.subscriptionName }}</td>
            </ng-container>

            <!-- Contact Name Column -->
            <ng-container matColumnDef="contactName">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Contact</th>
              <td mat-cell *matCellDef="let element">{{ element.contactName || '-' }}</td>
            </ng-container>

            <!-- Email Column -->
            <ng-container matColumnDef="email">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Email</th>
              <td mat-cell *matCellDef="let element">{{ element.email || '-' }}</td>
            </ng-container>

            <!-- Notification Count Column -->
            <ng-container matColumnDef="notificationCountInAlert">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Notifications</th>
              <td mat-cell *matCellDef="let element">{{ element.notificationCountInAlert }}</td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="alertsColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: alertsColumns;"></tr>
          </table>
        </div>

        <div *ngIf="!loadingAlertsPreview && alertsDataSource.data.length === 0" class="no-data-message">
          No alerts to preview
        </div>
      </mat-expansion-panel>
    </mat-accordion>
  </div>

  <!-- Invoices Section -->
  <div class="utility-section">
    <div class="section-header">
      <div class="section-title">
        <h2>Invoices</h2>
        <div class="test-mode-container" *ngIf="invoicesTestMode">
          <div class="test-mode-badge test-mode-active">
            <mat-icon class="test-mode-icon">warning</mat-icon>
            <span class="test-mode-text">TEST MODE</span>
          </div>
          <div class="test-mode-detail" *ngIf="testFinanceContactId">
            <mat-icon class="test-mode-detail-icon">badge</mat-icon>
            <span>Contact ID: <strong>{{ testFinanceContactId }}</strong></span>
          </div>
        </div>
      </div>
    </div>
    <div class="utility-description">
      <p>Preview all invoices due to send, select the ones you wish to process, then send them.</p>
    </div>

    <div class="button-container">
      <button
        mat-raised-button
        color="warn"
        (click)="previewInvoices()"
        [disabled]="isProcessing || loadingInvoicesPreview"
        class="utility-button preview-button">
        <mat-icon>preview</mat-icon>
        Preview Invoices
      </button>

      <button
        mat-raised-button
        color="accent"
        (click)="sendSelectedInvoices()"
        [disabled]="isProcessing || selectedInvoiceIds.length === 0"
        class="utility-button">
        <mat-icon>receipt</mat-icon>
        Send {{ selectedInvoiceIds.length }} Invoice{{ selectedInvoiceIds.length !== 1 ? 's' : '' }}
      </button>
    </div>

    <div class="invoice-date-override-row">
      <mat-checkbox [(ngModel)]="overrideInvoiceDate">Override invoice date</mat-checkbox>
      <input
        type="date"
        [(ngModel)]="invoiceDateOverride"
        [disabled]="!overrideInvoiceDate"
        class="date-override-input">
    </div>

    <!-- Invoices Preview Accordion -->
    <mat-accordion *ngIf="showInvoicesPreview" class="preview-accordion">
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <mat-icon>info</mat-icon>
            Invoices Preview ({{ invoicesDataSource.data.length }} records)
          </mat-panel-title>
        </mat-expansion-panel-header>
        
        <div *ngIf="loadingInvoicesPreview" class="loading-message">
          <mat-spinner diameter="30"></mat-spinner>
          Loading preview...
        </div>

        <div *ngIf="!loadingInvoicesPreview && invoicesDataSource.data.length > 0">
          <table mat-table [dataSource]="invoicesDataSource" matSort #invoicesSort class="preview-table">
            <!-- Select Column -->
            <ng-container matColumnDef="select">
              <th mat-header-cell *matHeaderCellDef>
                <mat-checkbox
                  [checked]="isAllInvoicesSelected()"
                  [indeterminate]="selectedInvoiceIds.length > 0 && !isAllInvoicesSelected()"
                  (change)="masterToggleInvoices()">
                </mat-checkbox>
              </th>
              <td mat-cell *matCellDef="let element">
                <mat-checkbox
                  [checked]="isInvoiceSelected(element.subscriptionId)"
                  (change)="toggleInvoiceSelection(element.subscriptionId)">
                </mat-checkbox>
              </td>
            </ng-container>

            <!-- Subscription Name Column -->
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Subscription</th>
              <td mat-cell *matCellDef="let element">{{ element.name }}</td>
            </ng-container>

            <!-- Service Type Column -->
            <ng-container matColumnDef="serviceTypeName">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Service Type</th>
              <td mat-cell *matCellDef="let element">{{ element.serviceTypeName }}</td>
            </ng-container>

            <!-- Start Date Column -->
            <ng-container matColumnDef="startDate">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Start Date</th>
              <td mat-cell *matCellDef="let element">{{ element.startDate ? (element.startDate | date:'dd/MM/yyyy') : '-' }}</td>
            </ng-container>

            <!-- Frequency Column -->
            <ng-container matColumnDef="invoiceFrequency">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Frequency</th>
              <td mat-cell *matCellDef="let element">{{ formatFrequency(element.invoiceFrequency) }}</td>
            </ng-container>

            <!-- Last Invoice Date Column -->
            <ng-container matColumnDef="lastInvoiceDate">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Last Invoice</th>
              <td mat-cell *matCellDef="let element">{{ element.lastInvoiceDate ? (element.lastInvoiceDate | date:'dd/MM/yyyy') : '-' }}</td>
            </ng-container>

            <!-- Days Since Last Invoice Column -->
            <ng-container matColumnDef="daysSinceLastInvoice">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Days Since Invoice</th>
              <td mat-cell *matCellDef="let element">{{ element.daysSinceLastInvoice ?? '-' }}</td>
            </ng-container>

            <!-- Notification Count Column -->
            <ng-container matColumnDef="notificationCount">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Notifications</th>
              <td mat-cell *matCellDef="let element">{{ element.notificationCount }}</td>
            </ng-container>

            <!-- Credits Column -->
            <ng-container matColumnDef="paymentCancelledNotificationCount">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Credits</th>
              <td mat-cell *matCellDef="let element">{{ element.paymentCancelledNotificationCount }}</td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="invoicesColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: invoicesColumns;"></tr>
          </table>
        </div>

        <div *ngIf="!loadingInvoicesPreview && invoicesDataSource.data.length === 0" class="no-data-message">
          No invoices to preview
        </div>
      </mat-expansion-panel>
    </mat-accordion>
  </div>
</div>
